
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ROLE_ROUTES } from "@utils/constants";
import {
  setSelectedAssignedFolderId,
  setSelectedAssignedProjectId,
} from "@store/slices/validatorAdminSlice";
import {
  fetchValidatorAdminProjects,
  fetchValidatorAdminFiles,
  fetchValidatorAdminFolders,
} from "@store/thunks/validatorAdminStructureThunk";

const ValidatorAdminSidebar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((store) => store.auth);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    assignedProjects = [],
    assignedFolders = {},
    assignedFiles = {},
    selectedAssignedProjectId,
    selectedAssignedFolderId,
  } = useSelector((store) => store.validatorAdmin);

  // Fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        await dispatch(fetchValidatorAdminProjects());
        setLoading(false);
      } catch (error) {
        console.error("Error fetching validator admin projects:", error);
        setError("Failed to fetch projects");
        setLoading(false);
      }
    };

    fetchProjects();
  }, [dispatch]);

  // Reset selections when navigating to root path
  useEffect(() => {
    if (location.pathname === ROLE_ROUTES.VALIDATOR_ADMIN) {
      dispatch(setSelectedAssignedProjectId(null));
      dispatch(setSelectedAssignedFolderId(null));
    }
  }, [location.pathname, dispatch]);

  // Select the first project on page reload
  useEffect(() => {
    const safeAssignedProjects = Array.isArray(assignedProjects) ? assignedProjects.filter(project => project.status !== "DELETED") : [];
    if (
      safeAssignedProjects.length > 0 &&
      !selectedAssignedProjectId &&
      location.pathname === ROLE_ROUTES.VALIDATOR_ADMIN
    ) {
      const firstProjectId = safeAssignedProjects[0].projectId;
      dispatch(setSelectedAssignedProjectId(firstProjectId));
    }
  }, [assignedProjects, selectedAssignedProjectId, location.pathname, dispatch]);

  const handleProjectClick = async (projectId) => {
    try {
      dispatch(setSelectedAssignedProjectId(projectId));
      await dispatch(fetchValidatorAdminFolders(projectId));
      navigate(`${ROLE_ROUTES.VALIDATOR_ADMIN}/folders`);
    } catch (error) {
      console.error("Error handling project click:", error);
    }
  };

  const handleFolderClick = async (folderId) => {
    try {
      dispatch(setSelectedAssignedFolderId(folderId));
      await dispatch(fetchValidatorAdminFiles(folderId));
      navigate(`${ROLE_ROUTES.VALIDATOR_ADMIN}/folders/files`);
    } catch (error) {
      console.error("Error handling folder click:", error);
    }
  };

  // Safe data access function
  const getSafeAssignedProjects = () => {
    return Array.isArray(assignedProjects) ? assignedProjects.filter(project => project.status !== "DELETED") : [];
  };

  const getSafeAssignedFolders = (projectId) => {
    const folders = assignedFolders[projectId];
    return Array.isArray(folders) ? folders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) : [];
  };

  const safeAssignedProjects = getSafeAssignedProjects();

  if (loading) {
    return (
      <div className="bg-white border-r border-gray-200 shadow-sm h-screen flex items-center justify-center p-4 sm:p-6">
        <p className="text-gray-500 text-sm font-['Semantic/Strong']">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border-r border-gray-200 shadow-sm h-screen p-4 sm:p-6">
        <h3 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900 mb-4">
          Projects & Folders
        </h3>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p className="font-['Semantic/Strong'] font-bold">Error</p>
          <p className="font-['Semantic/Strong']">{error}</p>
          <p className="font-['Semantic/Strong'] mt-2">Try refreshing the page or contact support.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border-r border-gray-200 shadow-sm h-screen overflow-y-auto w-full mt-12">
      <div className="p-4 sm:p-6">
        <h3 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900 mb-4">
          Projects & Folders
        </h3>
        {safeAssignedProjects.length > 0 ? (
          safeAssignedProjects
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .map((project) => {
              const isSelected = selectedAssignedProjectId === project.projectId;
              const foldersForProject = getSafeAssignedFolders(project.projectId);
              const foldersHaveBeenFetched = Array.isArray(assignedFolders[project.projectId]);

              return (
                <div key={project.projectId} className="mb-3">
                  {/* Project Level */}
                  <div
                    className={`cursor-pointer p-2 rounded-lg text-sm font-['Semantic/Strong'] transition-colors duration-200 ${isSelected ? "bg-[#ec7026] text-white" : "bg-gray-50 text-gray-700 hover:bg-gray-100"}`}
                    onClick={() => handleProjectClick(project.projectId)}
                  >
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                      <span className="truncate">{project.projectName}</span>
                    </div>
                  </div>

                  {/* Conditionally render folders ONLY if they have been fetched and are not empty */}
                  {isSelected && foldersHaveBeenFetched && foldersForProject.length > 0 && (
                    <div className="ml-4 mt-2 space-y-1">
                      {foldersForProject.map((folder) => (
                        <div
                          key={folder.folderId}
                          className={`cursor-pointer p-2 rounded-lg text-sm font-['Semantic/Strong'] transition-colors duration-200 ${selectedAssignedFolderId === folder.folderId ? "bg-[#ec7026]/10 text-[#ec7026]" : "bg-gray-50 text-gray-600 hover:bg-gray-100"}`}
                          onClick={() => handleFolderClick(folder.folderId)}
                        >
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="truncate">{folder.folderName}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Conditionally render "No folders" message ONLY if they have been fetched and the result is an empty array */}
                  {isSelected && foldersHaveBeenFetched && foldersForProject.length === 0 && (
                    <div className="ml-4 mt-2 p-2 text-gray-500 text-sm font-['Semantic/Strong']">
                      No folders available
                    </div>
                  )}
                </div>
              );
            })
        ) : (
          <div className="text-gray-500 text-sm font-['Semantic/Strong']">
            No projects available
          </div>
        )}
      </div>
    </div>
  );
};

export default ValidatorAdminSidebar;